import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Loader2, Youtube, Film } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import type { SavedVideo } from "@shared/models/savedVideos";

export type VideoDescriptor =
  | { type: "youtube"; youtubeId: string; title?: string; savedVideoId?: number }
  | { type: "cloudflare"; streamId: string; hlsUrl: string; title?: string; savedVideoId?: number }
  | null;

function isValidYouTubeId(id: string): boolean {
  return /^[a-zA-Z0-9_-]{8,12}$/.test(id);
}

function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && isValidYouTubeId(match[1])) {
      return match[1];
    }
  }
  return null;
}

interface VideoPickerDrawerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (descriptor: VideoDescriptor) => void;
  title?: string;
}

export function VideoPickerDrawer({ open, onClose, onSelect, title = "Pick a Video" }: VideoPickerDrawerProps) {
  const auth = useAuth();
  const [ytUrl, setYtUrl] = useState("");
  const [ytError, setYtError] = useState("");
  const [loadingStreamId, setLoadingStreamId] = useState<number | null>(null);

  const { data: savedVideos, isLoading: isLoadingVideos } = useQuery<SavedVideo[]>({
    queryKey: ["/api/videos"],
    enabled: auth.isAuthenticated && open,
  });

  const handleYouTubeLoad = () => {
    const id = extractYouTubeId(ytUrl.trim());
    if (id) {
      setYtError("");
      onSelect({ type: "youtube", youtubeId: id });
      setYtUrl("");
      onClose();
    } else {
      setYtError("Please enter a valid YouTube URL");
    }
  };

  const handleSelectVideo = async (video: SavedVideo) => {
    if (video.videoType === "youtube" && video.youtubeId) {
      onSelect({ type: "youtube", youtubeId: video.youtubeId, title: video.title ?? undefined, savedVideoId: video.id });
      onClose();
      return;
    }

    if (video.videoType === "upload" && video.cloudflareStreamId) {
      setLoadingStreamId(video.id);
      try {
        const res = await fetch(`/api/videos/${video.id}/stream`, { credentials: "same-origin" });
        if (!res.ok) throw new Error("Failed to get stream URL");
        const data = await res.json();
        if (data.hlsUrl) {
          onSelect({
            type: "cloudflare",
            streamId: video.cloudflareStreamId,
            hlsUrl: data.hlsUrl,
            title: video.title ?? undefined,
            savedVideoId: video.id,
          });
          onClose();
        }
      } catch {
        // ignore, stay open
      } finally {
        setLoadingStreamId(null);
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="bottom" className="max-h-[80vh] flex flex-col">
        <SheetHeader className="shrink-0">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        {/* YouTube URL input */}
        <div className="shrink-0 border-b pb-4 mb-4">
          <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1.5">
            <Youtube className="w-4 h-4" /> Load from YouTube URL
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="https://youtube.com/watch?v=..."
              value={ytUrl}
              onChange={(e) => { setYtUrl(e.target.value); setYtError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") handleYouTubeLoad(); }}
              className="flex-1"
            />
            <Button onClick={handleYouTubeLoad} disabled={!ytUrl.trim()}>
              Load
            </Button>
          </div>
          {ytError && <p className="text-xs text-destructive mt-1">{ytError}</p>}
        </div>

        {/* Library */}
        <div className="flex-1 overflow-y-auto">
          <p className="text-sm text-muted-foreground mb-3 flex items-center gap-1.5">
            <Film className="w-4 h-4" /> From My Library
          </p>
          {!auth.isAuthenticated ? (
            <p className="text-sm text-muted-foreground text-center py-6">Sign in to access your library</p>
          ) : isLoadingVideos ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : !savedVideos?.length ? (
            <p className="text-sm text-muted-foreground text-center py-6">No saved videos yet</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pb-4">
              {savedVideos.map((video) => (
                <button
                  key={video.id}
                  className="text-left rounded-lg border bg-card hover:bg-accent transition-colors overflow-hidden disabled:opacity-50"
                  onClick={() => handleSelectVideo(video)}
                  disabled={loadingStreamId === video.id}
                >
                  <div className="aspect-video bg-muted relative">
                    {video.thumbnailUrl ? (
                      <img
                        src={video.thumbnailUrl}
                        alt={video.title ?? ""}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                    {loadingStreamId === video.id && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 animate-spin text-white" />
                      </div>
                    )}
                    <span className="absolute top-1 right-1 text-[10px] bg-black/70 text-white px-1 rounded">
                      {video.videoType === "youtube" ? "YT" : "Upload"}
                    </span>
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-medium truncate">{video.title ?? "Untitled"}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
